(() => {
  /**
   * Vernal 神策埋点（全局脚本）
   * ================
   * 全局常量/状态约定
   * ================
   * - `window.__vernal`：由 `theme.liquid` 下发配置（serverUrl/options/debug/customerEmail 等）
   * - `window.__vernalSensorsState`：仅用于“当前页面生命周期”的幂等/缓存（不要依赖它做跨页面状态）
   *   - `presetProperties`：`sensors.quick('isReady')` 后缓存的 `getPresetProperties()`，供购物车 properties 等透传后端
   * - `window.__vernalSensorsGetPresetPropertiesForCart()`：返回可并入购物车 `properties` 的字段（含 `_sensorsPresetProperties` JSON 字符串）
   * - `window.__vernalSensorsEnsurePresetPropertiesForCart()`：加购前等待 SDK isReady 并缓存预制属性（避免首次加购为空）
   * - `window.__vernalSensorsEnsureCookieIdForCart()`：加购前以神策 SDK `$identity_cookie_id` 为准写入 `ver_sc_cid`（无法解析时返回空，不生成本地 UUID）
   */
  const STORAGE_KEYS = {
    // login 辅助：记录上次成功 login 的邮箱（仅作同步/排查，不能单独作为跳过 login 的依据）
    loggedInEmail: 'vernal_sensors_logged_in_email_v1'
  };

  // 保留：匿名标识相关能力（当前版本不使用，但后续可复用）
  const COOKIE_NAME = 'ver_sc_cid';
  const SENSORS_CROSS_COOKIE_NAME = 'sensorsdata2015jssdkcross';
  /** 用于全站跳转时传递「上一页标题」，供 PageView / PageLeave 的 referrer_page_name */
  const SESSION_LAST_PAGE_TITLE_KEY = 'vernal_sensors_last_page_title';

  /**
   * 读取“上一页标题”。
   * - 这里用 sessionStorage 做跨页面传递：离开页写入，进入页读取。
   * - 若 storage 不可用（隐私模式/禁用）则退化为空字符串。
   */
  function getSessionLastPageTitle() {
    try {
      return window.sessionStorage ? window.sessionStorage.getItem(SESSION_LAST_PAGE_TITLE_KEY) || '' : '';
    } catch {
      return '';
    }
  }

  /**
   * 写入“当前页标题”，供下个页面读取作为 referrer_page_name。
   * - 只做 best-effort：异常直接忽略，避免影响主流程。
   */
  function setSessionLastPageTitle(title) {
    try {
      if (window.sessionStorage) window.sessionStorage.setItem(SESSION_LAST_PAGE_TITLE_KEY, title || '');
    } catch {
      // ignore
    }
  }

  /** 物流追踪页（支持 /pages/tracking-order 或 /xx/pages/tracking-order） */
  function isTrackingOrderPage() {
    const p = (typeof location !== 'undefined' && location.pathname) || '';
    return /\/pages\/tracking-order\/?$/.test(p);
  }

  /** 页面地址：去掉 ? 及之后的查询参数（hash 一并去掉） */
  function getUrlWithoutQueryParams(href) {
    const raw = href == null ? '' : String(href);
    const q = raw.indexOf('?');
    return q === -1 ? raw : raw.slice(0, q);
  }

  /** 地址栏查询参数：无或空字符串时返回 null */
  function getUrlParamOrNull(name) {
    try {
      const u = new URL(window.location.href);
      const v = u.searchParams.get(name);
      if (v == null) return 'null';
      const t = String(v).trim();
      return t === '' ? 'null' : t;
    } catch {
      return 'null';
    }
  }

  /**
   * 物流追踪页专用：事件 TrackPageview
   * - referrer_page_name：上一页标题（与全站 session 一致；无则 null）
   * - utm_*：地址栏有值则传字符串，否则 null
   */
  function trackTrackingOrderPageview(sensors, cfg) {
    if (!isTrackingOrderPage()) return;
    const ref = getSessionLastPageTitle();
    const payload = {
      event_name: 'tracking-order-page',
      referrer_page_name: ref && String(ref).trim() !== '' ? String(ref).trim() : null,
      utm_platform: getUrlParamOrNull('utm_platform'),
      utm_context: getUrlParamOrNull('utm_context'),
      utm_element: getUrlParamOrNull('utm_element')
    };
    try {
      sensors.track('TrackPageview', payload);
    } catch (e) {
    }
  }

  /**
   * 自定义页面事件：PageView（进入）、PageLeave（离开，含停留时长）
   * referrer_page_name：来自上一次页面离开时写入的标题；首访或外链进入为空字符串。
   */
  function bindPageViewAndPageLeave(sensors, cfg) {
    const referrerPageName = getSessionLastPageTitle();
    const pageName = (typeof document !== 'undefined' && document.title) || '';
    let pageEnteredAt = nowMs();
    let pageLeaveStashed = false;
    const PENDING_PAGE_LEAVE_KEY = 'vernal_sensors_pending_page_leave_v1';

    function getCurrentNavigationType() {
      try {
        const navEntries =
          window.performance && typeof window.performance.getEntriesByType === 'function'
            ? window.performance.getEntriesByType('navigation')
            : null;
        if (navEntries && navEntries[0] && navEntries[0].type) {
          return String(navEntries[0].type).toLowerCase();
        }
      } catch {
        // ignore
      }
      try {
        const legacyNav = window.performance && window.performance.navigation;
        // 兼容旧接口：TYPE_RELOAD = 1
        if (legacyNav && legacyNav.type === 1) return 'reload';
      } catch {
        // ignore
      }
      return '';
    }

    function readPendingPageLeave() {
      try {
        if (!window.sessionStorage) return null;
        const raw = window.sessionStorage.getItem(PENDING_PAGE_LEAVE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
      } catch {
        return null;
      }
    }

    function clearPendingPageLeave() {
      try {
        if (window.sessionStorage) window.sessionStorage.removeItem(PENDING_PAGE_LEAVE_KEY);
      } catch {
        // ignore
      }
    }

    function stashPageLeaveForNextPage() {
      if (pageLeaveStashed) return;
      pageLeaveStashed = true;
      const durationMs = Math.max(0, nowMs() - pageEnteredAt);
      const pageDuration = Math.round(durationMs / 1000);
      const pendingPayload = {
        page_name: pageName,
        page_duration: pageDuration,
        referrer_page_name: referrerPageName
      };
      try {
        if (window.sessionStorage) {
          window.sessionStorage.setItem(PENDING_PAGE_LEAVE_KEY, JSON.stringify(pendingPayload));
        }
      } catch {
        // ignore
      }
    }

    /**
     * 在“新页面”补发上一页 PageLeave：
     * - 若当前页面是 reload，说明上一页离开是刷新，直接丢弃 pending，不上报
     * - 其余导航类型（navigate/back_forward）补发上一页离开
     */
    function flushPendingPageLeaveOnEntry(options) {
      const opts = options && typeof options === 'object' ? options : {};
      const pending = readPendingPageLeave();
      if (!pending) return;
      clearPendingPageLeave();
      if (!opts.ignoreReloadGuard && getCurrentNavigationType() === 'reload') {
        if (cfg.debug) console.log('[vernal-sensors] drop pending PageLeave for reload');
        return;
      }
      try {
        sensors.track('PageLeave', pending);
        if (cfg.debug) console.log('[vernal-sensors] flush pending PageLeave', pending);
      } catch (e) {
        if (cfg.debug) console.warn('[vernal-sensors] pending PageLeave track error', e);
      }
    }

    function scheduleFlushPendingPageLeave(reason, options) {
      const delays = [0, 40, 120, 260];
      delays.forEach((ms) => {
        window.setTimeout(() => {
          flushPendingPageLeaveOnEntry(options);
          if (cfg.debug && ms === 0) {
            console.log('[vernal-sensors] try flush pending PageLeave', { reason });
          }
        }, ms);
      });
    }

    // 先处理上一页的离开补发，再发送本页 PageView
    scheduleFlushPendingPageLeave('entry');

    /**
     * 供其它事件（如商品详情页浏览）复用本次进入页的 referrer。
     * 注意：`setSessionLastPageTitle(pageName)` 会立刻把 session 覆盖成“本页标题”，
     * 所以需要在这里先缓存一份“前向页标题”，否则后续就取不到了。
     */
    window.__vernalSensorsState = window.__vernalSensorsState || {};
    window.__vernalSensorsState.referrerPageName = referrerPageName;

    function trackPageView(refName) {
      try {
        sensors.track('PageView', {
          page_name: pageName,
          referrer_page_name: refName || ''
        });
      } catch (e) {
        if (cfg.debug) console.warn('[vernal-sensors] PageView track error', e);
      }
    }

    /**
     * PageView：仅做 best-effort，不让埋点影响页面功能。
     */
    trackPageView(referrerPageName);
    trackTrackingOrderPageview(sensors, cfg);
    setSessionLastPageTitle(pageName);

    /**
     * 浏览器后退/前进命中 bfcache 时，脚本不会重新执行：
     * - pageshow(persisted=true) 视为一次新的进入，补发 PageView
     * - 同时补发 pending PageLeave（来自刚离开的页面）
     * - 重置本页离开状态和进入时间，确保再次离开可记录 PageLeave
     */
    window.addEventListener(
      'pageshow',
      (event) => {
        if (!event || event.persisted !== true) return;
        scheduleFlushPendingPageLeave('pageshow_bfcache', { ignoreReloadGuard: true });
        const backReferrer = getSessionLastPageTitle();
        window.__vernalSensorsState = window.__vernalSensorsState || {};
        window.__vernalSensorsState.referrerPageName = backReferrer;
        trackPageView(backReferrer);
        trackTrackingOrderPageview(sensors, cfg);
        setSessionLastPageTitle(pageName);
        pageEnteredAt = nowMs();
        pageLeaveStashed = false;
      },
      { capture: true }
    );

    // 离开当前页时只写入 pending；由下一页决定是否补发（refresh 场景会被丢弃）
    window.addEventListener('pagehide', stashPageLeaveForNextPage, { capture: true });
    window.addEventListener('beforeunload', stashPageLeaveForNextPage, { capture: true });
  }

  /**
   * 当前时间戳（ms）。
   * - 保持单一出口，便于后续替换/打点时统一处理。
   */
  function nowMs() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  /**
   * localStorage 读：异常时退化为 null（不阻塞主流程）。
   */
  function getFromStorage(key) {
    try {
      return window.localStorage ? window.localStorage.getItem(key) : null;
    } catch {
      // 隐私模式/禁用存储时可能抛错：此时退化为“本次会话不做幂等”
      return null;
    }
  }

  /**
   * localStorage 写：异常直接忽略（不阻塞主流程）。
   */
  function setToStorage(key, value) {
    try {
      if (window.localStorage) window.localStorage.setItem(key, value);
    } catch {
      // ignore：存储不可用时不影响主流程
    }
  }

  /**
   * localStorage 删：异常直接忽略（不阻塞主流程）。
   */
  function removeFromStorage(key) {
    try {
      if (window.localStorage) window.localStorage.removeItem(key);
    } catch {
      // ignore：存储不可用时不影响主流程
    }
  }

  /**
   * Cookie 读取（简单解析）。
   */
  function getCookie(name) {
    const all = document.cookie ? document.cookie.split('; ') : [];
    for (let i = 0; i < all.length; i++) {
      const part = all[i];
      const idx = part.indexOf('=');
      if (idx === -1) continue;
      const k = decodeURIComponent(part.slice(0, idx));
      if (k === name) return decodeURIComponent(part.slice(idx + 1));
    }
    return null;
  }

  /**
   * Cookie 写入：SameSite=Lax，Path=/，按天数设置 Max-Age。
   */
  function setCookie(name, value, days) {
    const maxAge = Math.max(0, Math.floor(days * 86400));
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
  }

  /**
   * Cookie 删除：使用过去时间 + Max-Age=0 兜底。
   */
  function deleteCookie(name) {
    const encoded = encodeURIComponent(name);
    document.cookie = `${encoded}=; Max-Age=0; Path=/; SameSite=Lax`;
    document.cookie = `${encoded}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax`;
  }

  function getCookieDomainCandidates() {
    try {
      const host = (window.location && window.location.hostname ? String(window.location.hostname) : '').trim().toLowerCase();
      if (!host) return [];
      const parts = host.split('.').filter(Boolean);
      const set = new Set();
      set.add(host);
      set.add(`.${host}`);
      if (parts.length >= 2) {
        const base2 = parts.slice(-2).join('.');
        set.add(base2);
        set.add(`.${base2}`);
      }
      if (parts.length >= 3) {
        const base3 = parts.slice(-3).join('.');
        set.add(base3);
        set.add(`.${base3}`);
      }
      return Array.from(set);
    } catch {
      return [];
    }
  }

  function deleteCookieByDomain(name, domain) {
    const encoded = encodeURIComponent(name);
    document.cookie = `${encoded}=; Max-Age=0; Path=/; Domain=${domain}; SameSite=Lax`;
    document.cookie = `${encoded}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; Domain=${domain}; SameSite=Lax`;
  }

  function deleteCookieEverywhere(name) {
    deleteCookie(name);
    const domains = getCookieDomainCandidates();
    for (let i = 0; i < domains.length; i++) {
      deleteCookieByDomain(name, domains[i]);
    }
  }

  /**
   * 从预制属性对象读取 $identity_cookie_id（SDK 即时值或已缓存的 presetProperties）。
   */
  function extractIdentityCookieIdFromPreset(preset) {
    if (!preset || typeof preset !== 'object') return null;
    const ids = preset.identities;
    if (ids && typeof ids === 'object') {
      const v = ids['$identity_cookie_id'] != null ? ids['$identity_cookie_id'] : ids.$identity_cookie_id;
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    const top = preset['$identity_cookie_id'];
    if (top != null && String(top).trim() !== '') return String(top).trim();
    return null;
  }

  /**
   * 从神策 JS SDK 预置属性中读取 $identity_cookie_id（与上报体 identities 一致，非服务端 HTTP 回包字段）。
   */
  function getSensorsIdentityCookieId(sensors) {
    try {
      if (!sensors || typeof sensors.getPresetProperties !== 'function') return null;
      const preset = sensors.getPresetProperties();
      return extractIdentityCookieIdFromPreset(preset);
    } catch {
      return null;
    }
  }

  /** 将解析到的 cookieId 写入 `ver_sc_cid`，供 global.js 加购 properties 读取。 */
  function persistVernalCookieId(id) {
    const v = id != null ? String(id).trim() : '';
    if (!v) return;
    if (getCookie(COOKIE_NAME) === v) return;
    setCookie(COOKIE_NAME, v, 365);
  }

  /**
   * 即时同步 cookieId：SDK identities → 已缓存 preset → 写入 Cookie。
   * @returns {string|null} 写入后的 `ver_sc_cid`，无则 null
   */
  function syncCookieIdNow(sensors, cfg) {
    let sid = null;
    if (sensors) sid = getSensorsIdentityCookieId(sensors);
    if (!sid) {
      const preset = window.__vernalSensorsState && window.__vernalSensorsState.presetProperties;
      sid = extractIdentityCookieIdFromPreset(preset);
    }
    if (sid) persistVernalCookieId(sid);
    return getCookie(COOKIE_NAME);
  }

  /**
   * 从神策预制属性读取当前 SDK 绑定的 loginId（$identity_login_id）。
   * - 用于判断是否真的已 login，避免仅 localStorage 命中就跳过导致匿名态
   */
  function getSensorsLoginId(sensors) {
    try {
      if (!sensors || typeof sensors.getPresetProperties !== 'function') return null;
      const preset = sensors.getPresetProperties();
      if (!preset || typeof preset !== 'object') return null;
      const ids = preset.identities;
      if (ids && typeof ids === 'object') {
        const v = ids['$identity_login_id'] != null ? ids['$identity_login_id'] : ids.$identity_login_id;
        if (v != null && String(v).trim() !== '') return normalizeEmail(String(v));
      }
      const top = preset['$identity_login_id'];
      if (top != null && String(top).trim() !== '') return normalizeEmail(String(top));
      return null;
    } catch {
      return null;
    }
  }

  /** SDK 当前 loginId 是否与目标邮箱一致（已建立登录身份） */
  function isSensorsLoggedInAs(sensors, email) {
    const target = normalizeEmail(email);
    if (!target) return false;
    return getSensorsLoginId(sensors) === target;
  }

  /**
   * 将神策 $identity_cookie_id 写入 ver_sc_cid getOrCreateCookieId 同一 Cookie），供加购等业务读取。
   * SDK 在 init/autoTrack 后才会稳定写入 identities，故短时重试几次。
   */
  function syncVernalCookieFromSensorsIdentity(sensors, cfg) {
    const delays = [0, 50, 200, 800, 2000];
    delays.forEach((ms) => {
      window.setTimeout(() => {
        syncCookieIdNow(sensors, cfg);
      }, ms);
    });
  }

  /**
   * 生成 UUID v4。
   * - 优先使用 crypto.getRandomValues（更安全、分布更好）
   * - 否则退化到 Math.random（仅作匿名串联，不用于安全场景）
   */
  function uuidV4() {
    const cryptoObj = window.crypto || window.msCrypto;
    if (cryptoObj && cryptoObj.getRandomValues) {
      const buf = new Uint8Array(16);
      cryptoObj.getRandomValues(buf);
      buf[6] = (buf[6] & 0x0f) | 0x40;
      buf[8] = (buf[8] & 0x3f) | 0x80;
      const bth = [];
      for (let i = 0; i < 256; i++) bth[i] = (i + 0x100).toString(16).substr(1);
      return (
        bth[buf[0]] +
        bth[buf[1]] +
        bth[buf[2]] +
        bth[buf[3]] +
        '-' +
        bth[buf[4]] +
        bth[buf[5]] +
        '-' +
        bth[buf[6]] +
        bth[buf[7]] +
        '-' +
        bth[buf[8]] +
        bth[buf[9]] +
        '-' +
        bth[buf[10]] +
        bth[buf[11]] +
        bth[buf[12]] +
        bth[buf[13]] +
        bth[buf[14]] +
        bth[buf[15]]
      );
    }

    const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  }

  /**
   * 获取或生成匿名 cookieId（当前版本主要用于与业务 properties 对齐）。
   */
  function getOrCreateCookieId() {
    const existing = getCookie(COOKIE_NAME);
    if (existing) return existing;
    const id = uuidV4();
    setCookie(COOKIE_NAME, id, 365);
    return id;
  }

  /**
   * email 归一化：trim + 小写，用于 login 幂等判断。
   */
  function normalizeEmail(email) {
    return typeof email === 'string' ? email.trim().toLowerCase() : '';
  }

  /**
   * 从 `window.__vernal` 读取神策配置。
   * - 所有字段都允许为空：空则跳过初始化/上报。
   */
  function getConfig() {
    const v = window.__vernal || {};
    const sensorsCfg = v.sensors || {};
    return {
      shopKey: v.shopKey || null,
      customerEmail: v.customerEmail || null,
      sdkAssetUrl: v.sensorsSdkAssetUrl || '',
      serverUrl: sensorsCfg.serverUrl || null,
      options: sensorsCfg.options || null,
      sensorsDelayInit: !!v.sensorsDelayInit,
      // debug 由 theme.liquid 通过 query 参数开关控制
      debug: !!v.sensorsDebug
    };
  }

  /**
   * 读取 Shopify consent（best-effort）。
   * 可能值：yes / no / no_interaction / unavailable
   */
  function getTrackingConsentStatus() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.getTrackingConsent !== 'function') return 'unavailable';
      const v = privacy.getTrackingConsent();
      if (v === 'yes' || v === 'no' || v === 'no_interaction') return v;
      return 'unavailable';
    } catch {
      return 'unavailable';
    }
  }

  /**
   * 当前访客是否明确拒绝 tracking。
   */
  function isTrackingDeclined() {
    return getTrackingConsentStatus() === 'no';
  }

  /**
   * 是否允许调用 sensors.login。
   * - 默认保持原逻辑（允许 login）。
   * - 仅在开启「神策延迟初始化」且处于拒绝/匿名模式时禁止 login。
   */
  function canBindLoginIdentity(cfg) {
    if (!cfg || !cfg.sensorsDelayInit) return true;
    window.__vernalSensorsState = window.__vernalSensorsState || {};
    if (window.__vernalSensorsState.forceAnonymous) return false;
    return !isTrackingDeclined();
  }

  function clearVernalIdentityArtifacts() {
    deleteCookieEverywhere(COOKIE_NAME);
    deleteCookieEverywhere(SENSORS_CROSS_COOKIE_NAME);
    removeFromStorage(STORAGE_KEYS.loggedInEmail);
  }

  function trackCookieBannerClick(event, cfg) {
    const sensors = getSensors();
    if (!sensors || typeof sensors.track !== 'function') return;
    const detail = event && event.detail && typeof event.detail === 'object' ? event.detail : {};
    const clickTextRaw = detail.clickText == null ? '' : String(detail.clickText);
    const clickText = clickTextRaw.trim();
    const payload = {
      model_type: 'cookie_banner',
      click_text: clickText || null
    };
    try {
      sensors.track('PageModelClick', payload);
      if (cfg && cfg.debug) console.log('[vernal-sensors] PageModelClick(cookie-banner)', payload);
    } catch (e) {
      if (cfg && cfg.debug) console.warn('[vernal-sensors] PageModelClick(cookie-banner) track error', e);
    }
  }

  function trackCookieBannerClickByText(clickText, cfg) {
    const sensors = getSensors();
    if (!sensors || typeof sensors.track !== 'function') return;
    const txt = clickText == null ? '' : String(clickText).trim();
    const payload = {
      model_type: 'cookie_banner',
      click_text: txt || null
    };
    try {
      sensors.track('PageModelClick', payload);
      if (cfg && cfg.debug) console.log('[vernal-sensors] PageModelClick(cookie-banner)', payload);
    } catch (e) {
      if (cfg && cfg.debug) console.warn('[vernal-sensors] PageModelClick(cookie-banner) track error', e);
    }
  }

  function shouldTrackCookieBannerClick(event) {
    if (!event || !event.detail || typeof event.detail !== 'object') return false;
    if (event.detail.forceTrack === true) return true;
    const clickText = event.detail.clickText == null ? '' : String(event.detail.clickText).trim();
    return clickText !== '';
  }

  function getSensorsAnonymousIdentity(sensors) {
    try {
      if (!sensors || typeof sensors.getPresetProperties !== 'function') return '';
      const preset = sensors.getPresetProperties();
      if (!preset || typeof preset !== 'object') return '';
      const ids = preset.identities;
      if (!ids || typeof ids !== 'object') return '';
      const v = ids['$identity_anonymous_id'] != null ? ids['$identity_anonymous_id'] : ids.$identity_anonymous_id;
      return v == null ? '' : String(v).trim();
    } catch {
      return '';
    }
  }

  function resetSensorsIdentityToAnonymous(sensors, cfg) {
    if (!sensors || typeof sensors !== 'object') return;
    try {
      if (typeof sensors.logout === 'function') sensors.logout();
    } catch (e) {
      if (cfg && cfg.debug) console.warn('[vernal-sensors] logout error', e);
    }
    try {
      if (typeof sensors.resetAnonymousIdentity === 'function') sensors.resetAnonymousIdentity();
    } catch (e) {
      if (cfg && cfg.debug) console.warn('[vernal-sensors] resetAnonymousIdentity error', e);
    }
    try {
      if (typeof sensors.clearAllRegister === 'function') sensors.clearAllRegister();
    } catch (e) {
      if (cfg && cfg.debug) console.warn('[vernal-sensors] clearAllRegister error', e);
    }
  }

  /**
   * 获取神策 SDK 全局对象。
   * - 这里的名字取决于你们接入的 SDK 版本/打包方式。
   */
  function getSensors() {
    return window.sensorsDataAnalytic201505 || window['sensorsDataAnalytic201505'] || null;
  }

  function ensureSensorsSdkLoaded(cfg) {
    const ready = getSensors();
    if (ready) return Promise.resolve(true);
    if (!cfg || !cfg.sdkAssetUrl) return Promise.resolve(false);

    window.__vernalSensorsState = window.__vernalSensorsState || {};
    if (window.__vernalSensorsState.sdkLoadPromise) return window.__vernalSensorsState.sdkLoadPromise;

    window.__vernalSensorsState.sdkLoadPromise = new Promise((resolve) => {
      const finish = (ok) => resolve(!!ok);
      const existingByMark = document.querySelector('script[data-vernal-sensors-sdk="1"]');
      const existingBySrc = document.querySelector(`script[src="${cfg.sdkAssetUrl}"]`);
      const existing = existingByMark || existingBySrc;
      if (existing) {
        if (getSensors()) {
          finish(true);
          return;
        }
        existing.addEventListener('load', () => finish(!!getSensors()), { once: true });
        existing.addEventListener('error', () => finish(false), { once: true });
        window.setTimeout(() => finish(!!getSensors()), 12000);
        return;
      }

      const script = document.createElement('script');
      script.src = cfg.sdkAssetUrl;
      script.defer = true;
      script.async = true;
      script.setAttribute('data-vernal-sensors-sdk', '1');
      script.addEventListener('load', () => finish(!!getSensors()), { once: true });
      script.addEventListener('error', () => finish(false), { once: true });
      document.head.appendChild(script);
      window.setTimeout(() => finish(!!getSensors()), 12000);
    });

    return window.__vernalSensorsState.sdkLoadPromise;
  }

  function hasCachedPresetProperties() {
    const preset = window.__vernalSensorsState && window.__vernalSensorsState.presetProperties;
    return !!(preset && typeof preset === 'object' && Object.keys(preset).length > 0);
  }

  /**
   * 尝试从 SDK 读取并写入 `__vernalSensorsState.presetProperties`。
   * @returns {boolean} 是否成功缓存非空预制属性
   */
  function tryCachePresetPropertiesFromSensors(sensors, cfg) {
    if (!sensors || typeof sensors.getPresetProperties !== 'function') return false;
    try {
      const presetProperties = sensors.getPresetProperties();
      if (!presetProperties || typeof presetProperties !== 'object' || !Object.keys(presetProperties).length) {
        return false;
      }
      window.__vernalSensorsState = window.__vernalSensorsState || {};
      window.__vernalSensorsState.presetProperties = presetProperties;
      if (cfg && cfg.debug) console.log('[vernal-sensors] presetProperties cached');
      return true;
    } catch (e) {
      if (cfg && cfg.debug) console.warn('[vernal-sensors] getPresetProperties error', e);
      return false;
    }
  }

  /**
   * 在 SDK ready 后拉取并缓存神策预制属性（getPresetProperties），供业务侧透传后端。
   */
  function cacheSensorsPresetPropertiesOnReady(sensors, cfg) {
    if (!sensors || typeof sensors.quick !== 'function') return;
    try {
      sensors.quick('isReady', function () {
        tryCachePresetPropertiesFromSensors(sensors, cfg);
      });
    } catch (e) {
      if (cfg.debug) console.warn('[vernal-sensors] quick isReady error', e);
    }
  }

  /**
   * 加购前等待预制属性就绪（首次进入页面时 SDK 常在 isReady 之后才返回有效值）。
   * @param {number} [maxWaitMs=2000]
   * @returns {Promise<boolean>}
   */
  function ensurePresetPropertiesCachedForCart(maxWaitMs = 2000) {
    const cfg = getConfig();
    if (hasCachedPresetProperties()) return Promise.resolve(true);

    const sensors = getSensors();
    if (!sensors) return Promise.resolve(false);

    if (tryCachePresetPropertiesFromSensors(sensors, cfg)) return Promise.resolve(true);

    return new Promise((resolve) => {
      let settled = false;
      const finish = (ok) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(!!ok);
      };

      const timer = window.setTimeout(() => {
        finish(tryCachePresetPropertiesFromSensors(sensors, cfg));
      }, maxWaitMs);

      try {
        if (typeof sensors.quick === 'function') {
          sensors.quick('isReady', function () {
            finish(tryCachePresetPropertiesFromSensors(sensors, cfg));
          });
        } else {
          finish(tryCachePresetPropertiesFromSensors(sensors, cfg));
        }
      } catch {
        finish(tryCachePresetPropertiesFromSensors(sensors, cfg));
      }
    });
  }

  /**
   * 解析加购应使用的 cookieId：仅以神策 SDK 身份为准，不生成本地 UUID。
   * - SDK 已加载：认 $identity_cookie_id（即时读取或已缓存 preset）
   * - SDK 未加载：仅读取已同步的 ver_sc_cid，无则返回空字符串
   * @returns {string}
   */
  function resolveCartCookieId(sensors, cfg) {
    if (sensors) {
      let sid = getSensorsIdentityCookieId(sensors);
      if (!sid) {
        const preset = window.__vernalSensorsState && window.__vernalSensorsState.presetProperties;
        sid = extractIdentityCookieIdFromPreset(preset);
      }
      if (sid) {
        persistVernalCookieId(sid);
        return sid;
      }
      syncCookieIdNow(sensors, cfg);
      return getCookie(COOKIE_NAME) || '';
    }

    return getCookie(COOKIE_NAME) || '';
  }

  /**
   * 加购前确保 `ver_sc_cid` 与神策 SDK 身份一致（与预制属性等待窗口对齐）。
   * 即使 ver_sc_cid 已存在也会校验并覆盖，避免历史本地 UUID 与 SDK 上报身份脱节。
   * @param {number} [maxWaitMs=2000]
   * @returns {Promise<string>}
   */
  function ensureCookieIdForCart(maxWaitMs = 2000) {
    const cfg = getConfig();
    const sensors = getSensors();
    const presetPromise = ensurePresetPropertiesCachedForCart(maxWaitMs);

    const finish = () => resolveCartCookieId(sensors, cfg);

    if (!sensors) {
      return presetPromise.then(() => finish());
    }

    return presetPromise.then(() => {
      const immediate = finish();
      if (immediate) return immediate;

      return new Promise((resolve) => {
        const startedAt = nowMs();
        const interval = window.setInterval(() => {
          const id = finish();
          if (id || nowMs() - startedAt >= maxWaitMs) {
            window.clearInterval(interval);
            resolve(id || finish());
          }
        }, 100);
      });
    });
  }

  /**
   * 供购物车 line item `properties` 合并：优先用 isReady 缓存，否则尝试即时读取 SDK。
   */
  function getPresetPropertiesPayloadForCart() {
    const sensors = getSensors();
    const cfg = getConfig();
    let preset = null;
    const state = window.__vernalSensorsState || {};
    if (state.presetProperties && typeof state.presetProperties === 'object') {
      preset = state.presetProperties;
    } else if (sensors) {
      tryCachePresetPropertiesFromSensors(sensors, cfg);
      preset = (window.__vernalSensorsState && window.__vernalSensorsState.presetProperties) || null;
    }
    if (!preset || typeof preset !== 'object' || !Object.keys(preset).length) return {};
    try {
      return { _sensorsPresetProperties: JSON.stringify(preset) };
    } catch {
      return {};
    }
  }

  /**
   * registerPage 动态公共属性：shop
   * - 按站点返回固定枚举字符串，便于神策侧统一分组/过滤
   * - 采用函数返回值的写法，符合神策官方“动态公共属性”用法
   */
  function getShopRegisterPageValue(cfg) {
    const k = (cfg && cfg.shopKey ? String(cfg.shopKey) : '').trim().toLowerCase();
    if (k === 'us') return 'VERNAL_SHOPIFY_US';
    if (k === 'de') return 'VERNAL_SHOPIFY_DE';
    if (k === 'uk') return 'VERNAL_SHOPIFY_UK';
    return 'VERNAL_SHOPIFY_UNKNOWN';
  }

  /**
   * 获取币种代码（如 USD/EUR/GBP）。
   * - 优先读主题的 shopSettings（你们现有全局变量）
   * - 其次读 Shopify.currency.active
   */
  function getCurrencyCode() {
    return (
      (window.shopSettings && window.shopSettings.isoCode) ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      ''
    );
  }

  /**
   * 获取 checkout 路径。
   * - 兼容多语言前缀 root（如 /uk/checkout）
   */
  function getCheckoutUrl() {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      if (root && root !== '/') return `${root.replace(/\/$/, '')}/checkout`;
      return '/checkout';
    } catch {
      return '/checkout';
    }
  }

  /**
   * 拉取最新购物车快照。
   * - checkout 埋点需要拿到“点击结算瞬间”的购物车明细
   */
  function fetchCartJson() {
    const url = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart.js`;
    return fetch(url, { credentials: 'same-origin' }).then((r) => r.json());
  }

  /**
   * 判断节点是否仍在 DOM 上。
   * - 用于定时器触发时避免对已被替换/销毁的节点继续上报。
   */
  function isElementConnected(el) {
    try {
      return !!(el && el.isConnected);
    } catch {
      return false;
    }
  }

  /**
   * 从 dataset 读取字符串并 trim；空值返回 null。
   * - 统一入口便于兜底与异常处理。
   */
  function getDatasetString(el, key) {
    try {
      const v = el && el.dataset ? el.dataset[key] : null;
      if (v == null) return null;
      const t = String(v).trim();
      return t === '' ? null : t;
    } catch {
      return null;
    }
  }

  /**
   * 曝光可见性兜底：
   * - 过滤关闭态 modal 内元素（DOM 在、但用户不可见）
   * - 过滤 display:none / visibility:hidden / opacity:0 的祖先链
   */
  function isHiddenForImpression(el) {
    try {
      if (!el || !isElementConnected(el)) return true;
      if (el.closest && el.closest('modal-dialog.product-popup-modal:not([open])')) return true;
      let cur = el;
      while (cur && cur !== document.documentElement) {
        if (cur.nodeType === 1) {
          const style = window.getComputedStyle(cur);
          if (style) {
            if (style.display === 'none') return true;
            if (style.visibility === 'hidden' || style.visibility === 'collapse') return true;
            if (Number(style.opacity) === 0) return true;
          }
        }
        cur = cur.parentElement;
      }
      return false;
    } catch {
      return false;
    }
  }

  function intersectsRect(a, b) {
    const left = Math.max(a.left, b.left);
    const top = Math.max(a.top, b.top);
    const right = Math.min(a.right, b.right);
    const bottom = Math.min(a.bottom, b.bottom);
    return {
      left,
      top,
      right,
      bottom,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top)
    };
  }

  function clipsChildrenByOverflow(style) {
    if (!style) return false;
    const ox = style.overflowX || '';
    const oy = style.overflowY || '';
    const v = style.overflow || '';
    const s = `${ox} ${oy} ${v}`.toLowerCase();
    return s.includes('hidden') || s.includes('scroll') || s.includes('auto') || s.includes('clip');
  }

  /**
   * 商品块「仅曝光、不上报 ProductClick」。
   * - Liquid：`data-vernal-impression-only="true"`（对应 dataset.vernalImpressionOnly）
   */
  function isProductImpressionOnly(el) {
    const v = getDatasetString(el, 'vernalImpressionOnly');
    if (!v) return false;
    const s = v.toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }

  function isSequentialProductClickDedupeEnabled(el) {
    const v = getDatasetString(el, 'vernalClickDedupeSequential');
    if (!v) return false;
    const s = v.toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }

  /**
   * 商品曝光/点击上报需要的完整 payload（含 belong_area）。
   * - belong_area 由各模块 Liquid 侧下发（例如 section 标题）
   */
  function buildProductPayloadFromEl(el) {
    const productId = getDatasetString(el, 'productId');
    const variantId = getDatasetString(el, 'variantId');
    const productName = getDatasetString(el, 'productName');
    const belongArea = getDatasetString(el, 'belongArea');
    if (!productId || !variantId || !productName || !belongArea) return null;
    return {
      product_id: productId,
      variant_id: variantId,
      product_name: productName,
      belong_area: belongArea
    };
  }

  /**
   * 商品基础信息（不含 belong_area）。
   * - 用于详情页浏览等事件：字段集合不同，不强依赖 belong_area。
   */
  function buildProductCoreFromEl(el) {
    const productId = getDatasetString(el, 'productId');
    const variantId = getDatasetString(el, 'variantId');
    const productName = getDatasetString(el, 'productName');
    if (!productId || !variantId || !productName) return null;
    return {
      product_id: productId,
      variant_id: variantId,
      product_name: productName,
    };
  }

  /**
   * 计算元素在 viewport 中的“可视面积占比”（0~1）。
   * - IntersectionObserver 在懒加载/重排时 ratio 可能不及时，这里作为实时兜底。
   * - 用面积（宽*高）而非高度占比：更符合“露出 70%”的直觉口径。
   */
  function getViewportIntersectionRatio(el) {
    try {
      if (!el || typeof el.getBoundingClientRect !== 'function') return 0;
      if (isHiddenForImpression(el)) return 0;
      const r = el.getBoundingClientRect();
      const w = r.width || 0;
      const h = r.height || 0;
      if (w <= 0 || h <= 0) return 0;

      const vw = window.innerWidth || document.documentElement.clientWidth || 0;
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      if (vw <= 0 || vh <= 0) return 0;
      let visibleRect = intersectsRect(r, {
        left: 0,
        top: 0,
        right: vw,
        bottom: vh
      });
      if (visibleRect.width <= 0 || visibleRect.height <= 0) return 0;

      // 把会裁剪子元素的滚动/隐藏容器纳入交集，避免“只露一点也算满面积”。
      let parent = el.parentElement;
      while (parent && parent !== document.documentElement) {
        const style = window.getComputedStyle(parent);
        if (clipsChildrenByOverflow(style)) {
          const pr = parent.getBoundingClientRect();
          visibleRect = intersectsRect(visibleRect, pr);
          if (visibleRect.width <= 0 || visibleRect.height <= 0) return 0;
        }
        parent = parent.parentElement;
      }

      const intersectionArea = visibleRect.width * visibleRect.height;
      const area = w * h;
      if (area <= 0) return 0;
      return intersectionArea / area;
    } catch {
      return 0;
    }
  }

  function bindProductImpressionAndClick(sensors, cfg) {
    if (!sensors || typeof sensors.track !== 'function') return;

    /**
     * 商品卡片统一选择器：
     * - 由 Liquid 侧在商品块根节点下发 data 属性（data-vernal-sensors="product"）
     * - 仅曝光不上报点击：根节点加 `data-vernal-impression-only="true"`
     */
    const SELECTOR = '[data-vernal-sensors~="product"]';
    const PRODUCT_CLICK_SELECTOR = '[data-vernal-sensors~="productClick"]';
    const observed = new WeakSet();
    const stateByEl = new WeakMap();
    const lastFireByEl = new WeakMap(); // element -> ts
    const lastProductClickKeyByScope = new WeakMap(); // scope -> key

    const SHORT_SUPPRESS_MS = 1000; // 防轮播 clone/同帧重复
    const IMPRESSION_THRESHOLD = 0.7;
    const IMPRESSION_DWELL_MS = 500;
    const OUT_OF_VIEW_EPSILON = 0.001; // 视作“完全离开视口”

    /**
     * key 用于“短时间抑制重复触发”，避免轮播 clone 或同一帧多次回调导致的重复上报。
     * 这里不做跨次去重（你选择的是 per_view）。
     */
    function canFireNow(el) {
      const now = nowMs();
      const last = lastFireByEl.get(el);
      if (last != null && now - last < SHORT_SUPPRESS_MS) return false;
      lastFireByEl.set(el, now);
      return true;
    }

    /**
     * 曝光计时器：离开阈值时取消，避免滑过也触发。
     */
    function clearTimerForEl(el) {
      const st = stateByEl.get(el);
      if (st && st.timer) {
        window.clearTimeout(st.timer);
        st.timer = null;
      }
    }

    function ensureState(el) {
      let st = stateByEl.get(el);
      if (!st) {
        st = {
          ratio: 0,
          hasFired: false,
          timer: null,
        };
        stateByEl.set(el, st);
      }
      return st;
    }

    /**
     * dwell 到期后做一次视口校验并上报 ProductImpression。
     */
    function finishImpressionDwellCheck(el) {
      if (!isElementConnected(el)) return;
      const st = ensureState(el);
      if (st.hasFired) return;
      const payload = buildProductPayloadFromEl(el);
      if (!payload) return;
      const liveRatio = getViewportIntersectionRatio(el);
      if (cfg.debug) {
        console.log('[vernal-sensors] ProductImpression check', {
          ratio: Number(liveRatio.toFixed(3)),
          threshold: IMPRESSION_THRESHOLD,
          product_id: payload.product_id,
          variant_id: payload.variant_id,
          belong_area: payload.belong_area
        });
      }
      if (liveRatio <= OUT_OF_VIEW_EPSILON) {
        st.hasFired = false;
        return;
      }
      if (liveRatio < IMPRESSION_THRESHOLD) return;
      if (!canFireNow(el)) return;
      try {
        sensors.track('ProductImpression', payload);
        st.hasFired = true;
        if (cfg.debug) console.log('[vernal-sensors] ProductImpression', payload);
      } catch (e) {
        if (cfg.debug) console.warn('[vernal-sensors] ProductImpression track error', e);
      }
    }

    /**
     * 根据可视比例更新状态并启动/取消 dwell（IO 与弹窗打开后的手动 nudge 共用）。
     */
    function syncProductCardIntersectionState(el, ratio) {
      const st = ensureState(el);
      st.ratio = ratio;
      if (st.ratio <= OUT_OF_VIEW_EPSILON) {
        clearTimerForEl(el);
        st.hasFired = false;
        return;
      }
      if (st.hasFired) return;
      if (st.ratio >= IMPRESSION_THRESHOLD) {
        if (st.timer) return;
        st.timer = window.setTimeout(() => {
          st.timer = null;
          finishImpressionDwellCheck(el);
        }, IMPRESSION_DWELL_MS);
      } else {
        clearTimerForEl(el);
      }
    }

    /**
     * IntersectionObserver：用于“进入阈值时启动 dwell timer”。
     * - 真实触发点仍会在 timer 到期时用 getBoundingClientRect 再校验一次。
     */
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const ratioFromIO = entry.intersectionRatio || 0;
          const ratio = Math.max(ratioFromIO, getViewportIntersectionRatio(el));
          syncProductCardIntersectionState(el, ratio);
        });
      },
      { threshold: [0, IMPRESSION_THRESHOLD, 1] }
    );

    /**
     * 对单个元素注册观察（WeakSet 防止重复 observe）。
     */
    function observeEl(el) {
      if (!el || observed.has(el)) return;
      observed.add(el);
      ensureState(el);
      try {
        io.observe(el);
      } catch {
        // ignore
      }
    }

    /**
     * 扫描一个容器内的所有商品卡并注册观察。
     * - 首次页面加载、以及 DOM 动态更新（facets 替换/轮播 clone）都会调用。
     */
    function observeAllIn(root) {
      const scope = root && root.querySelectorAll ? root : document;
      if (!scope || !scope.querySelectorAll) return;
      const els = scope.querySelectorAll(SELECTOR);
      els.forEach(observeEl);
    }

    /**
     * 弹窗/抽屉从隐藏变为显示时，IntersectionObserver 往往要等滚动才补回调。
     * - 对容器内商品：先 unobserve/observe 强制重算，再在双 rAF 后用 getBoundingClientRect 同步一次比例。
     * - 主题里 `modal-dialog` 通过 `open` 属性显示，见 MutationObserver 对 `attributeFilter: ['open']`。
     */
    function nudgeProductImpressionsIn(root) {
      const base = root && root.querySelectorAll ? root : document;
      if (!base || !base.querySelectorAll) return;
      const els = base.querySelectorAll(SELECTOR);
      els.forEach((el) => {
        observeEl(el);
        if (!observed.has(el)) return;
        try {
          io.unobserve(el);
          io.observe(el);
        } catch {
          // ignore
        }
      });
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          els.forEach((el) => {
            if (!observed.has(el)) return;
            const ratio = getViewportIntersectionRatio(el);
            syncProductCardIntersectionState(el, ratio);
          });
        });
      });
    }

    /**
     * 轮播切屏属于“新一屏曝光”，即使有重复商品也需要重新统计当前屏曝光。
     * 因此在箭头点击后，先重置该轮播容器内商品的本轮曝光状态，再进入 nudge 流程。
     */
    function resetImpressionCycleIn(root) {
      const base = root && root.querySelectorAll ? root : document;
      if (!base || !base.querySelectorAll) return;
      const els = base.querySelectorAll(SELECTOR);
      els.forEach((el) => {
        const st = ensureState(el);
        clearTimerForEl(el);
        st.hasFired = false;
        try {
          lastFireByEl.delete(el);
        } catch {
          // ignore
        }
      });
    }

    /**
     * 轮播切换（尤其是首页 Best Seller）通常是 transform 动画，不会触发 DOM 结构变更。
     * 这里在箭头点击后分帧补几次 nudge，确保新露出的商品都能进入曝光判断。
     */
    function scheduleCarouselNudgeFrom(btnEl) {
      if (!btnEl || !btnEl.closest) return;
      const scope =
        btnEl.closest('.card-carousel') ||
        btnEl.closest('.swiper') ||
        btnEl.closest('[data-vernal-impression-scope]') ||
        document;
      resetImpressionCycleIn(scope);
      const delays = [0, 120, 280, 450, 700, 950];
      delays.forEach((ms) => {
        window.setTimeout(() => {
          nudgeProductImpressionsIn(scope);
        }, ms);
      });
    }

    /**
     * modal 打开时常伴随动画/异步布局，单次 nudge 容易漏判。
     * 打开时重置该 modal 内曝光周期并分帧补算，确保配件列表稳定触发曝光。
     */
    function scheduleModalOpenNudge(modalEl) {
      if (!modalEl) return;
      resetImpressionCycleIn(modalEl);
      const delays = [0, 120, 260, 420, 700];
      delays.forEach((ms) => {
        window.setTimeout(() => {
          nudgeProductImpressionsIn(modalEl);
        }, ms);
      });
    }

    /**
     * 仅对真正的弹窗容器处理 open 属性变化，避免点击商品导致的其他 open 变更误触发曝光重算。
     */
    function isModalDialogOpenTarget(el) {
      if (!el || el.nodeType !== 1 || !el.matches) return false;
      return el.matches('modal-dialog');
    }

    function getProductClickDedupeScope(el) {
      if (!el || !el.closest) return document;
      return el.closest('[data-vernal-click-dedupe-scope]') || document;
    }

    function clearLastProductClickKeyInScope(scope) {
      const s = scope || document;
      lastProductClickKeyByScope.delete(s);
    }

    function buildProductClickDedupeKey(payload) {
      return `${payload.product_id}::${payload.variant_id}::${payload.belong_area}`;
    }

    function shouldSuppressSequentialProductClick(el, payload) {
      if (!isSequentialProductClickDedupeEnabled(el)) return false;
      const scope = getProductClickDedupeScope(el);
      const key = buildProductClickDedupeKey(payload);
      const previous = lastProductClickKeyByScope.get(scope);
      if (previous === key) return true;
      lastProductClickKeyByScope.set(scope, key);
      return false;
    }

    /**
     * 禁用态商品列不触发点击曝光：
     * - 兼容配件列表的 `unfit`
     * - 兼容通用 disabled / aria-disabled
     */
    function isDisabledProductClickTarget(el) {
      if (!el) return false;
      if (el.hasAttribute && el.hasAttribute('disabled')) return true;
      const ariaDisabled = getDatasetString(el, 'ariaDisabled') || (el.getAttribute && el.getAttribute('aria-disabled'));
      if (ariaDisabled && String(ariaDisabled).toLowerCase() === 'true') return true;
      if (el.classList) {
        if (el.classList.contains('unfit')) return true;
        if (el.classList.contains('disabled')) return true;
      }
      return false;
    }

    // initial
    observeAllIn(document);

    try {
      window.__vernalSensorsRefreshProductImpression = function (maybeRoot) {
        nudgeProductImpressionsIn(maybeRoot || document);
      };
    } catch {
      // ignore
    }

    /**
     * 点击埋点：事件委托（capture）。
     * - capture 能更早拿到事件，避免被内部 stopPropagation 影响。
     * - 当前口径：点击卡片内任意元素均算点击（如需仅主链接，可收窄 selector）。
     */
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        const el = target.closest(SELECTOR);
        if (!el) return;
        if (isProductImpressionOnly(el)) return;
        const payload = buildProductPayloadFromEl(el);
        if (!payload) return;
        try {
          sensors.track('ProductClick', payload);
          if (cfg.debug) console.log('[vernal-sensors] ProductClick', payload);
        } catch (e) {
          if (cfg.debug) console.warn('[vernal-sensors] ProductClick track error', e);
        }
      },
      { capture: true }
    );

    /**
     * 按钮点击埋点：用于“卡片仅曝光，点击只认 Buy Now 按钮”的模块。
     * - Liquid 在按钮节点下发 data-vernal-sensors="productClick" 及商品字段。
     */
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        const dedupeScope = target.closest('[data-vernal-click-dedupe-scope]');
        const btn = target.closest(PRODUCT_CLICK_SELECTOR);
        if (!btn) {
          if (dedupeScope) clearLastProductClickKeyInScope(dedupeScope);
          return;
        }
        if (isDisabledProductClickTarget(btn)) {
          clearLastProductClickKeyInScope(getProductClickDedupeScope(btn));
          return;
        }
        const payload = buildProductPayloadFromEl(btn);
        if (!payload) {
          clearLastProductClickKeyInScope(getProductClickDedupeScope(btn));
          return;
        }
        if (shouldSuppressSequentialProductClick(btn, payload)) return;
        try {
          sensors.track('ProductClick', payload);
          if (cfg.debug) console.log('[vernal-sensors] ProductClick(button)', payload);
        } catch (e) {
          if (cfg.debug) console.warn('[vernal-sensors] ProductClick(button) track error', e);
        }
      },
      { capture: true }
    );

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        const arrow = target.closest('.swiper-button-next, .swiper-button-prev');
        if (!arrow) return;
        scheduleCarouselNudgeFrom(arrow);
      },
      { capture: true }
    );

    /**
     * DOM 动态更新：自动扫描新增节点并注册曝光观察。
     * - facets 会整段替换 ProductGridContainer
     * - carousel 可能 clone slides
     * - 仅 `modal-dialog` 的 open 变化会触发曝光补算
     */
    const mo = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        if (m.type === 'attributes' && m.attributeName === 'open' && m.target && m.target.nodeType === 1) {
          const targetEl = m.target;
          if (!isModalDialogOpenTarget(targetEl)) continue;
          if (targetEl.hasAttribute && targetEl.hasAttribute('open')) {
            scheduleModalOpenNudge(targetEl);
          } else {
            nudgeProductImpressionsIn(targetEl);
          }
        }
        if (!m.addedNodes || m.addedNodes.length === 0) continue;
        m.addedNodes.forEach((node) => {
          if (!node || node.nodeType !== 1) return; // element only
          const el = node;
          if (el.matches && el.matches(SELECTOR)) observeEl(el);
          observeAllIn(el);
        });
      }
    });

    try {
      mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    } catch {
      // ignore
    }
  }

  function bindProductDetailPageView(sensors, cfg) {
    if (!sensors || typeof sensors.track !== 'function') return;

    const SELECTOR = '[data-vernal-sensors="productDetail"]';

    // 详情页事件字段与列表页不同：belong_area -> referrer_page_name
    const referrerPageName =
      (window.__vernalSensorsState && window.__vernalSensorsState.referrerPageName) || getSessionLastPageTitle() || '';

    window.__vernalSensorsState = window.__vernalSensorsState || {};
    if (window.__vernalSensorsState.productDetailPageViewSent) return;

    /**
     * 详情页浏览：要求停留 0.5s 才上报。
     * 这里先做短轮询等待元素出现（某些主题块/脚本会延迟渲染），
     * 再 setTimeout 500ms 确认用户确实停留。
     */
    const startedAt = nowMs();
    const timeoutMs = 5000;
    const intervalMs = 50;
    const timer = window.setInterval(() => {
      if (window.__vernalSensorsState.productDetailPageViewSent) {
        window.clearInterval(timer);
        return;
      }
      if (nowMs() - startedAt > timeoutMs) {
        if (cfg.debug) console.warn('[vernal-sensors] ProductDetailPageView: element not found in time');
        window.clearInterval(timer);
        return;
      }
      const el = document.querySelector(SELECTOR);
      if (!el) return;
      const core = buildProductCoreFromEl(el);
      if (!core) return;

      window.clearInterval(timer);

      const detailPayload = {
        ...core,
        referrer_page_name: referrerPageName
      };

      window.setTimeout(() => {
        if (window.__vernalSensorsState.productDetailPageViewSent) return;
        if (document.visibilityState && document.visibilityState === 'hidden') return;
        if (!isElementConnected(el)) return;
        try {
          sensors.track('ProductDetailPageView', detailPayload);
          window.__vernalSensorsState.productDetailPageViewSent = true;
          if (cfg.debug) console.log('[vernal-sensors] ProductDetailPageView', detailPayload);
        } catch (e) {
          if (cfg.debug) console.warn('[vernal-sensors] ProductDetailPageView track error', e);
        }
      }, 500);
    }, intervalMs);
  }

  /**
   * 页面模块点击：PageModelClick
   * - Liquid 在可点击节点下发 data-vernal-sensors="pageModelClick"
   * - data-model-type / data-click-text / data-click-url
   */
  function buildPageModelClickPayloadFromEl(el) {
    const modelType = getDatasetString(el, 'modelType');
    if (!modelType) return null;
    const clickText = getDatasetString(el, 'clickText');
    const clickUrl = getDatasetString(el, 'clickUrl');
    const imageUrl = getDatasetString(el, 'imageUrl');
    return {
      model_type: modelType,
      click_text: clickText || '',
      click_url: clickUrl || '',
      image_url: imageUrl || ''
    };
  }

  /**
   * 自定义页面事件：PageEventTrack
   * - event_name：业务事件标识（如 diy_exceed_cabinet_num）
   */
  function trackPageEventTrack(sensors, cfg, eventName) {
    if (!sensors || typeof sensors.track !== 'function') return;
    const name = eventName == null ? '' : String(eventName).trim();
    if (!name) return;
    try {
      sensors.track('PageEventTrack', { event_name: name });
      if (cfg && cfg.debug) console.log('[vernal-sensors] PageEventTrack', { event_name: name });
    } catch (e) {
      if (cfg && cfg.debug) console.warn('[vernal-sensors] PageEventTrack track error', e);
    }
  }

  function observeOlcMaxPopover(sensors, cfg, popover) {
    if (!popover || popover._vernalSensorsMaxPopoverObserved) return;
    popover._vernalSensorsMaxPopoverObserved = true;
    const eventName = popover.getAttribute('data-vernal-sensors-page-event-name') || 'diy_exceed_cabinet_num';
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'hidden' && popover.hidden === false) {
          trackPageEventTrack(sensors, cfg, eventName);
        }
      }
    });
    observer.observe(popover, { attributes: true, attributeFilter: ['hidden'] });
  }

  /**
   * DIY 柜子配置器：超出最大槽位弹窗显示时上报 PageEventTrack
   * - Liquid 在 [data-olc-max-popover] 下发 data-vernal-sensors-page-event-name
   */
  function bindOfficeLayoutConfiguratorPageEvents(sensors, cfg) {
    if (!sensors || typeof sensors.track !== 'function') return;

    window.__vernalSensorsState = window.__vernalSensorsState || {};
    if (window.__vernalSensorsState.olcPageEventsBinded) return;
    window.__vernalSensorsState.olcPageEventsBinded = true;

    function scan() {
      document.querySelectorAll('[data-olc-max-popover]').forEach((el) => {
        observeOlcMaxPopover(sensors, cfg, el);
      });
    }

    scan();
    document.addEventListener('office-layout-configurator:ready', scan);
    document.addEventListener('shopify:section:load', scan);
  }

  function bindPageModelClick(sensors, cfg) {
    if (!sensors || typeof sensors.track !== 'function') return;

    const SELECTOR = '[data-vernal-sensors~="pageModelClick"]';

    window.__vernalSensorsState = window.__vernalSensorsState || {};
    if (window.__vernalSensorsState.pageModelClickBinded) return;
    window.__vernalSensorsState.pageModelClickBinded = true;

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        const el = target.closest(SELECTOR);
        if (!el) return;
        const payload = buildPageModelClickPayloadFromEl(el);
        if (!payload) return;
        try {
          sensors.track('PageModelClick', payload);
          if (cfg.debug) console.log('[vernal-sensors] PageModelClick', payload);
        } catch (e) {
          if (cfg.debug) console.warn('[vernal-sensors] PageModelClick track error', e);
        }
      },
      { capture: true }
    );
  }

  /**
   * 页面模块浏览：PageModelView（含 model_duration）
   * - 时长用起止时间戳差值，不设 interval，避免空转
   * - 3D：弹窗 open 记起点，close / 去掉 open 时上报
   * - 视频：play 记起点，pause / ended 时上报（ended 后浏览器也会 pause，用标记去重）
   * - model_duration 与 page_duration 一致，单位为秒
   */
  function trackPageModelView(sensors, cfg, modelType, durationMs) {
    if (!sensors || typeof sensors.track !== 'function') return;
    const type = modelType == null ? '' : String(modelType).trim();
    if (!type) return;
    const modelDuration = Math.max(0, Math.round((Number(durationMs) || 0) / 1000));
    const payload = {
      model_type: type,
      model_duration: modelDuration
    };
    try {
      sensors.track('PageModelView', payload);
      if (cfg.debug) console.log('[vernal-sensors] PageModelView', payload);
    } catch (e) {
      if (cfg.debug) console.warn('[vernal-sensors] PageModelView track error', e);
    }
  }

  function bindPageModelView(sensors, cfg) {
    if (!sensors || typeof sensors.track !== 'function') return;

    window.__vernalSensorsState = window.__vernalSensorsState || {};
    if (window.__vernalSensorsState.pageModelViewBinded) return;
    window.__vernalSensorsState.pageModelViewBinded = true;

    const MODEL_VIEW_SELECTOR = '[data-vernal-sensors-model-view]';
    const viewStartedAt = new WeakMap();
    const viewReportedForEnd = new WeakSet();

    function startViewSession(el) {
      if (!el) return;
      viewStartedAt.set(el, nowMs());
      viewReportedForEnd.delete(el);
    }

    function endViewSession(el) {
      if (!el) return;
      const startedAt = viewStartedAt.get(el);
      if (startedAt == null) return;
      viewStartedAt.delete(el);
      const modelType = el.getAttribute('data-vernal-sensors-model-view') || '';
      trackPageModelView(sensors, cfg, modelType, nowMs() - startedAt);
    }

    function resolveModalFromTrigger(trigger) {
      if (!trigger) return null;
      const modalId = trigger.getAttribute('data-desk-3d-open');
      if (!modalId) return null;
      return document.getElementById(modalId);
    }

    function observeDesk3dModal(modal) {
      if (!modal || modal._vernalSensorsModelViewObserved) return;
      modal._vernalSensorsModelViewObserved = true;

      modal.addEventListener('close', () => endViewSession(modal));

      if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(() => {
          if (modal.hasAttribute('open')) {
            startViewSession(modal);
          } else {
            endViewSession(modal);
          }
        });
        observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
      }

      if (modal.hasAttribute('open')) startViewSession(modal);
    }

    function scanDesk3dModals() {
      document
        .querySelectorAll(`${MODEL_VIEW_SELECTOR}[data-desk-3d-modal], modal-dialog${MODEL_VIEW_SELECTOR}`)
        .forEach(observeDesk3dModal);
    }

    scanDesk3dModals();
    document.addEventListener('shopify:section:load', scanDesk3dModals);

    // 点击 3D 入口时主动记起点，避免仅依赖 MutationObserver 漏记
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!target || !target.closest) return;
        const trigger = target.closest('[data-desk-3d-open]');
        if (!trigger) return;
        const modal = resolveModalFromTrigger(trigger);
        if (!modal) return;
        observeDesk3dModal(modal);
        startViewSession(modal);
      },
      true
    );

    function resolveVideoHost(video) {
      if (!video || !video.closest) return null;
      return video.closest(MODEL_VIEW_SELECTOR);
    }

    function onVideoPlay(event) {
      const video = event.target;
      if (!video || video.tagName !== 'VIDEO') return;
      const host = resolveVideoHost(video);
      if (!host) return;
      startViewSession(host);
    }

    function onVideoPause(event) {
      const video = event.target;
      if (!video || video.tagName !== 'VIDEO') return;
      const host = resolveVideoHost(video);
      if (!host) return;
      // ended 后通常会再触发 pause，避免同一段时长双报
      if (video.ended && viewReportedForEnd.has(host)) return;
      if (video.ended) viewReportedForEnd.add(host);
      endViewSession(host);
    }

    function onVideoEnded(event) {
      const video = event.target;
      if (!video || video.tagName !== 'VIDEO') return;
      const host = resolveVideoHost(video);
      if (!host) return;
      if (viewReportedForEnd.has(host)) return;
      viewReportedForEnd.add(host);
      endViewSession(host);
    }

    function bindVideoElement(video) {
      if (!video || video.tagName !== 'VIDEO' || video._vernalSensorsModelViewBound) return;
      if (!resolveVideoHost(video)) return;
      video._vernalSensorsModelViewBound = true;
      video.addEventListener('play', onVideoPlay);
      video.addEventListener('pause', onVideoPause);
      video.addEventListener('ended', onVideoEnded);
      if (!video.paused && !video.ended) {
        startViewSession(resolveVideoHost(video));
      }
    }

    function scanVideos(root) {
      const scope = root && root.querySelectorAll ? root : document;
      if (scope.tagName === 'VIDEO') {
        bindVideoElement(scope);
        return;
      }
      scope.querySelectorAll(`${MODEL_VIEW_SELECTOR} video`).forEach(bindVideoElement);
    }

    // 捕获阶段兜底 + 节点直绑，兼容部分浏览器媒体事件不冒泡的情况
    document.addEventListener('play', onVideoPlay, true);
    document.addEventListener('pause', onVideoPause, true);
    document.addEventListener('ended', onVideoEnded, true);
    scanVideos(document);

    if (typeof MutationObserver !== 'undefined') {
      const videoObserver = new MutationObserver((mutations) => {
        for (let i = 0; i < mutations.length; i++) {
          const nodes = mutations[i].addedNodes;
          for (let j = 0; j < nodes.length; j++) {
            const node = nodes[j];
            if (!node || node.nodeType !== 1) continue;
            scanVideos(node);
          }
        }
      });
      videoObserver.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  function bindShoppingCartCheckout(sensors, cfg) {
    if (!sensors || typeof sensors.track !== 'function') return;

    /**
     * 结算按钮 selector：
     * - 优先匹配我们在 Liquid 上显式标记的 data-vernal-sensors="checkout"
     * - 同时兼容主题内已有的 checkout 按钮/表单命名
     */
    const SELECTOR =
      '[data-vernal-sensors="checkout"], .mini-cart__checkout-button, .cart__checkout-button, button[name="checkout"], [name="checkout"]';

    window.__vernalSensorsState = window.__vernalSensorsState || {};
    if (window.__vernalSensorsState.checkoutBinded) return;
    window.__vernalSensorsState.checkoutBinded = true;

    /**
     * 结算权威路径走主题 cart.js（Bundler checkout + unfit 确认），避免裸 form.submit 绕过折扣规则。
     * 无 cart-items 时再 fallback 到 form submit / /checkout。
     */
    function submitOrRedirect(targetEl) {
      const form = targetEl && targetEl.closest ? targetEl.closest('form') : null;
      const cartItems =
        (form && form.querySelector && form.querySelector('cart-items')) ||
        document.querySelector('cart-items');

      if (cartItems && typeof cartItems.handleCheckout === 'function') {
        cartItems.handleCheckout({
          preventDefault() {},
          stopPropagation() {},
        });
        return;
      }

      if (typeof window.proceedToBundlerCheckout === 'function') {
        window.proceedToBundlerCheckout();
        return;
      }

      if (form && typeof form.submit === 'function') {
        try {
          if (!form.querySelector('input[name="checkout"]')) {
            const inp = document.createElement('input');
            inp.type = 'hidden';
            inp.name = 'checkout';
            inp.value = 'checkout';
            form.appendChild(inp);
          }
        } catch {
          // ignore
        }
        form.submit();
        return;
      }
      window.location.href = getCheckoutUrl();
    }

    document.addEventListener(
      'click',
      (event) => {
        const t = event.target;
        if (!t || !t.closest) return;
        const el = t.closest(SELECTOR);
        if (!el) return;
        if (el.disabled) return;

        // Programmatic checkout clicks from cart.js should not re-enter this interceptor
        if (window.__skipVernalCheckoutIntercept) return;

        // 避免重复触发（双击/冒泡）
        if (window.__vernalSensorsState.checkoutHandling) return;
        window.__vernalSensorsState.checkoutHandling = true;

        event.preventDefault();
        event.stopPropagation();

        // Loading style only — keep button enabled so Bundler can click it if needed
        try {
          el.classList.add('loading');
        } catch {
          // ignore
        }

        const currency = getCurrencyCode();

        /**
         * done() 必须幂等：
         * - hardTimeout(250ms) 与 fetch.finally 都可能触发；重复调用会结账两次
         * - Bundler 可能再次 click 结算按钮；用 __skipVernalCheckoutIntercept 抑制二次埋点
         */
        let checkoutContinueSettled = false;
        const done = () => {
          if (checkoutContinueSettled) return;
          checkoutContinueSettled = true;
          window.clearTimeout(hardTimeout);
          window.__skipVernalCheckoutIntercept = true;
          window.__vernalSensorsState.checkoutHandling = false;
          try {
            submitOrRedirect(el);
          } finally {
            window.setTimeout(() => {
              window.__skipVernalCheckoutIntercept = false;
            }, 3000);
          }
        };

        /**
         * checkout 对体验敏感：不应该因为埋点阻塞结算。
         * 因此这里硬超时 250ms：到点就继续结算，即使埋点尚未完成。
         */
        const hardTimeout = window.setTimeout(done, 250);

        fetchCartJson()
          .then((cart) => {
            // 3s 内只上报一次，防止 Bundler 回点 / 重复进监听导致双报
            const now = Date.now();
            if (
              window.__vernalSensorsState.checkoutTrackedAt &&
              now - window.__vernalSensorsState.checkoutTrackedAt < 3000
            ) {
              return;
            }
            window.__vernalSensorsState.checkoutTrackedAt = now;

            try {
              const items = Array.isArray(cart && cart.items) ? cart.items : [];
              const totalNumber = Number(cart && cart.item_count) || items.reduce((s, it) => s + Number(it.quantity || 0), 0);
              sensors.track('ShoppingCartCheckout', { product_number: totalNumber });
              if (cfg.debug) console.log('[vernal-sensors] ShoppingCartCheckout', { product_number: totalNumber });

              items.forEach((it) => {
                const productId = it && (it.product_id || it.productId) ? String(it.product_id || it.productId) : null;
                const variantId = it && it.id != null ? String(it.id) : null;
                const productName = it && (it.product_title || it.productTitle || it.title) ? String(it.product_title || it.productTitle || it.title) : null;
                const qty = Number(it && it.quantity) || 0;
                const priceCents = Number(it && (it.final_price ?? it.price ?? 0)) || 0;
                const displayPrice = priceCents / 100;
                if (!productId || !variantId || !productName || !qty) return;

                /**
                 * 结算详情需要“按商品拆分上报”，因此每个 line item 单独 track 一条。
                 * - product_actual_pay_amounts 用 display_price * qty（不含运费/税/折扣的进一步拆分口径）
                 */
                sensors.track('ShoppingCartCheckoutDetail', {
                  product_id: productId,
                  variant_id: variantId,
                  product_name: productName,
                  display_price: displayPrice,
                  curency: currency,
                  product_number: qty,
                  product_actual_pay_amounts: displayPrice * qty,
                });
              });
              if (cfg.debug) console.log('[vernal-sensors] ShoppingCartCheckoutDetail count', items.length);
            } catch (e) {
              if (cfg.debug) console.warn('[vernal-sensors] ShoppingCartCheckout track error', e);
            }
          })
          .catch((e) => {
            if (cfg.debug) console.warn('[vernal-sensors] fetchCartJson error', e);
          })
          .finally(() => {
            done();
          });
      },
      { capture: true }
    );
  }

  function initSensorsOnce(sensors, cfg) {
    if (!cfg.serverUrl) return false;
    /**
     * 注意：这里不能用 localStorage 跨页面做 init 幂等。
     * 因为页面刷新后 JS 运行环境会重置，如果跳过 init/autoTrack，会导致该次访问完全没有全埋点采集。
     * 我们只做“同一页面生命周期内”的幂等，避免脚本被重复执行时二次 init。
     */
    window.__vernalSensorsState = window.__vernalSensorsState || {};
    if (window.__vernalSensorsState.inited) return true;

    // options 由 theme.liquid 下发；这里做一次兜底，避免非对象导致 init 报错
    const options = cfg.options && typeof cfg.options === 'object' ? cfg.options : {};
    // console.log('----------------------------------init----------------------------------');
    sensors.init({
      server_url: cfg.serverUrl,
      ...options
    });

    // registerPage 用于补充当前页和 referrer（对部分 SPA/跳转场景更可控）
    // console.log('----------------------------------registerPage----------------------------------');
    sensors.registerPage({
      current_url: location.href,
      address_url: function () {
        return getUrlWithoutQueryParams(location.href);
      },
      referrer: document.referrer,
      shop: function () {
        return getShopRegisterPageValue(cfg);
      }
    });

    // 全埋点：自动采集页面浏览、点击等（取决于 SDK 配置）
    // console.log('----------------------------------autoTrack----------------------------------');
    sensors.quick('autoTrack');
    tryCachePresetPropertiesFromSensors(sensors, cfg);
    cacheSensorsPresetPropertiesOnReady(sensors, cfg);
    syncVernalCookieFromSensorsIdentity(sensors, cfg);
    window.__vernalSensorsState.inited = true;
    return true;
  }

  /**
   * 登录上报：神策官方建议在用户登录时调用 sensors.login(loginId) 建立登录身份
   * 这里以 customer.email 作为 loginId。
   * 幂等以 SDK 的 $identity_login_id 为准：localStorage 曾写过不代表当前 SDK 仍绑定（清 Cookie 后会丢态）。
   */
  function reportLoginOnce(sensors, cfg) {
    if (!canBindLoginIdentity(cfg)) {
      if (cfg.debug) console.log('[vernal-sensors] login skipped (consent/anonymous policy)');
      return;
    }

    const email = normalizeEmail(cfg.customerEmail);
    if (!email) return;

    if (isSensorsLoggedInAs(sensors, email)) {
      setToStorage(STORAGE_KEYS.loggedInEmail, email);
      if (cfg.debug) console.log('[vernal-sensors] login skipped (SDK already bound)', { loginId: email });
      return;
    }

    const lastStored = normalizeEmail(getFromStorage(STORAGE_KEYS.loggedInEmail));
    if (cfg.debug && lastStored === email) {
      console.log('[vernal-sensors] login: localStorage matched but SDK not bound, re-binding', { loginId: email });
    }

    try {
      sensors.login(email);
      setToStorage(STORAGE_KEYS.loggedInEmail, email);
      if (cfg.debug) console.log('[vernal-sensors] login', { loginId: email });
    } catch (e) {
      if (cfg.debug) console.warn('[vernal-sensors] login error', e);
    }
  }

  function runSensorsBootPolling(cfg, forceStart) {
    window.__vernalSensorsState = window.__vernalSensorsState || {};
    if (window.__vernalSensorsState.inited) {
      const sensors = getSensors();
      if (sensors) reportLoginOnce(sensors, cfg);
      return true;
    }

    if (window.__vernalSensorsState.bootStarted) {
      return true;
    }

    window.__vernalSensorsState.bootStarted = true;
    const startedAt = nowMs();
    const timeoutMs = 10000;
    const intervalMs = 50;
    if (cfg.debug) console.log('[vernal-sensors] boot', { timeoutMs, intervalMs, shopKey: cfg.shopKey, forceStart: !!forceStart });

    /**
     * 为什么要轮询等待：
     * - SDK 脚本是 `defer` 加载，理论上会按顺序执行
     * - 但在极端情况下（网络、缓存、阻塞）仍可能出现业务脚本先运行而 SDK 未就绪
     * 因此这里用短间隔轮询，最多等待 10s，避免 storefront 被卡住。
     */
    const timer = window.setInterval(() => {
      const sensors = getSensors();
      if (sensors) {
        try {
          const ok = initSensorsOnce(sensors, cfg);
          if (ok) {
            if (cfg.sensorsDelayInit && isTrackingDeclined()) {
              handleConsentDecline();
            }
            /**
             * 绑定顺序说明：
             * - login 依赖 init
             * - PageView/PageLeave 需要尽早绑定
             * - 业务事件（曝光/详情/checkout）在 init 成功后统一绑定
             */
            reportLoginOnce(sensors, cfg);
            bindPageViewAndPageLeave(sensors, cfg);
            bindProductImpressionAndClick(sensors, cfg);
            bindProductDetailPageView(sensors, cfg);
            bindPageModelClick(sensors, cfg);
            bindPageModelView(sensors, cfg);
            bindOfficeLayoutConfiguratorPageEvents(sensors, cfg);
            bindShoppingCartCheckout(sensors, cfg);
          }
        } catch (e) {
          if (cfg.debug) console.warn('[vernal-sensors] error', e);
        } finally {
          window.__vernalSensorsState.bootStarted = false;
          window.clearInterval(timer);
        }
        return;
      }

      if (nowMs() - startedAt > timeoutMs) {
        if (cfg.debug) console.warn('[vernal-sensors] timeout waiting for sensors SDK');
        window.__vernalSensorsState.bootStarted = false;
        window.clearInterval(timer);
      }
    }, intervalMs);
    return true;
  }

  function startSensorsBoot(forceStart) {
    const cfg = getConfig();
    if (cfg.sensorsDelayInit && !forceStart) return false;

    if (!cfg.sensorsDelayInit) {
      return runSensorsBootPolling(cfg, forceStart);
    }

    ensureSensorsSdkLoaded(cfg).then((loaded) => {
      if (!loaded) {
        if (cfg.debug) console.warn('[vernal-sensors] sdk load failed');
        return;
      }
      runSensorsBootPolling(cfg, forceStart);
    });
    return true;
  }

  function handleConsentAccept(event) {
    const cfg = getConfig();
    if (shouldTrackCookieBannerClick(event)) trackCookieBannerClick(event, cfg);
    if (!cfg.sensorsDelayInit) return;
    window.__vernalSensorsState = window.__vernalSensorsState || {};
    window.__vernalSensorsState.forceAnonymous = false;
    startSensorsBoot(true);
  }

  function handleConsentDecline(event) {
    const cfg = getConfig();
    if (shouldTrackCookieBannerClick(event)) trackCookieBannerClick(event, cfg);
    if (!cfg.sensorsDelayInit) return;
    window.__vernalSensorsState = window.__vernalSensorsState || {};
    window.__vernalSensorsState.forceAnonymous = true;
    clearVernalIdentityArtifacts();

    const sensors = getSensors();
    if (sensors) {
      const beforeAnonymousId = getSensorsAnonymousIdentity(sensors);
      resetSensorsIdentityToAnonymous(sensors, cfg);
      tryCachePresetPropertiesFromSensors(sensors, cfg);
      syncCookieIdNow(sensors, cfg);
      const afterAnonymousId = getSensorsAnonymousIdentity(sensors);
      if (
        beforeAnonymousId &&
        afterAnonymousId &&
        beforeAnonymousId === afterAnonymousId &&
        !window.__vernalSensorsState.declineReloadTriggered
      ) {
        window.__vernalSensorsState.declineReloadTriggered = true;
        window.setTimeout(() => {
          window.location.reload();
        }, 80);
      }
    }

    if (cfg.debug) console.log('[vernal-sensors] switched to anonymous mode after decline');
  }

  function stopConsentStatusPoll() {
    window.__vernalSensorsState = window.__vernalSensorsState || {};
    if (window.__vernalSensorsState.consentPollTimer) {
      window.clearInterval(window.__vernalSensorsState.consentPollTimer);
      window.__vernalSensorsState.consentPollTimer = null;
    }
  }

  /**
   * 仅在「神策延迟初始化」且访客尚未做出同意选择时，短周期兜底轮询 consent。
   * - 主路径依赖 Shopify / CMP 事件，避免每 500ms 调用 getTrackingConsent（会触发 Monorail produce 刷屏）。
   * - 拿到 yes/no 或超时后自动停止。
   */
  function startConsentStatusPollIfNeeded(cfg, processStatus) {
    stopConsentStatusPoll();
    if (!cfg.sensorsDelayInit) return;

    const status = window.__vernalSensorsState.lastConsentStatus;
    if (status !== 'no_interaction' && status !== 'unavailable') return;

    const pollIntervalMs = 3000;
    const pollTimeoutMs = 120000;
    const startedAt = nowMs();

    window.__vernalSensorsState.consentPollTimer = window.setInterval(() => {
      processStatus('poll');
      const current = window.__vernalSensorsState.lastConsentStatus;
      if (current === 'yes' || current === 'no') {
        stopConsentStatusPoll();
        return;
      }
      if (nowMs() - startedAt >= pollTimeoutMs) {
        if (cfg.debug) console.log('[vernal-sensors] consent poll stopped (timeout)');
        stopConsentStatusPoll();
      }
    }, pollIntervalMs);
  }

  function observeConsentStateChanges() {
    const cfg = getConfig();
    window.__vernalSensorsState = window.__vernalSensorsState || {};
    if (window.__vernalSensorsState.consentObserverStarted) return;
    window.__vernalSensorsState.consentObserverStarted = true;
    window.__vernalSensorsState.lastConsentStatus = getTrackingConsentStatus();

    if (cfg.sensorsDelayInit && window.__vernalSensorsState.lastConsentStatus === 'yes') {
      handleConsentAccept({ detail: { clickText: '', forceTrack: false, source: 'init_yes' } });
    } else if (cfg.sensorsDelayInit && window.__vernalSensorsState.lastConsentStatus === 'no') {
      handleConsentDecline({ detail: { clickText: '', forceTrack: false, source: 'init_no' } });
    }

    const processStatus = (source) => {
      const current = getTrackingConsentStatus();
      const prev = window.__vernalSensorsState.lastConsentStatus;
      if (current === prev) return;
      window.__vernalSensorsState.lastConsentStatus = current;
      if (current === 'yes') {
        stopConsentStatusPoll();
        handleConsentAccept({ detail: { clickText: 'Accept', forceTrack: true, source } });
      } else if (current === 'no') {
        stopConsentStatusPoll();
        handleConsentDecline({ detail: { clickText: 'Decline', forceTrack: true, source } });
      }
    };

    const scheduleProcess = (source) => {
      window.setTimeout(() => processStatus(source), 0);
      window.setTimeout(() => processStatus(source), 80);
    };

    document.addEventListener('visitorConsentCollected', () => scheduleProcess('visitorConsentCollected'));
    document.addEventListener('trackingConsentAccepted', () => scheduleProcess('trackingConsentAccepted'));
    document.addEventListener('trackingConsentDeclined', () => scheduleProcess('trackingConsentDeclined'));

    startConsentStatusPollIfNeeded(cfg, processStatus);
  }

  window.__vernalSensorsGetPresetPropertiesForCart = getPresetPropertiesPayloadForCart;
  window.__vernalSensorsEnsurePresetPropertiesForCart = ensurePresetPropertiesCachedForCart;
  window.__vernalSensorsEnsureCookieIdForCart = ensureCookieIdForCart;
  window.__vernalSensorsTrackCookieBannerClick = function (clickText) {
    trackCookieBannerClickByText(clickText, getConfig());
  };
  window.__vernalSensorsTrackPageEvent = function (eventName) {
    trackPageEventTrack(getSensors(), getConfig(), eventName);
  };
  window.__vernalSensorsStartAfterConsent = handleConsentAccept;
  window.__vernalSensorsHandleDecline = handleConsentDecline;

  document.addEventListener('vernal:consent-accept', handleConsentAccept);
  document.addEventListener('vernal:consent-decline', handleConsentDecline);
  observeConsentStateChanges();

  // 入口：执行一次启动流程（内部已做幂等与超时）
  startSensorsBoot(false);
})();
